import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';

import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from '../../../../components/inputs/RootFractionInput';
import RootFractionPiInput, {
  parseRootFractionPiInput,
  formatRootFractionPiPreview,
} from '../../../../components/inputs/RootFractionPiInput';
import { ConeAxialHintDiagram } from '../podtemat2_zamkniete_types/MaturaConeHeightClosedTask';
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


const SLANT_VALUES = [4, 6, 8, 10, 12];
const ANGLE_VALUES = [60, 90, 120];

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function rawString(value) {
  if (value == null) return '';
  if (typeof value === 'object' && value.raw != null) return String(value.raw);
  return String(value);
}

function approxEqual(a, b, eps = 1e-6) {
  return Number.isFinite(a) && Number.isFinite(b) && Math.abs(a - b) <= eps;
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x || 1;
}

function parseNumberLike(value) {
  return parseRootFractionInput(rawString(value));
}

function parseVolumeCoefficient(value) {
  return parseRootFractionPiInput(rawString(value));
}

function answerHasPi(value) {
  if (value && typeof value === 'object' && typeof value.hasPi === 'boolean') {
    return value.hasPi;
  }
  return /(?:\\pi|π|Π|pi)/i.test(rawString(value));
}

function sqrt3Latex(k) {
  if (k === 1) return '\\sqrt{3}';
  return `${k}\\sqrt{3}`;
}

function fracSqrtLatex(num, den, radicand) {
  const g = gcd(num, den);
  const n = num / g;
  const d = den / g;

  if (d === 1) {
    if (n === 1) return `\\sqrt{${radicand}}`;
    return `${n}\\sqrt{${radicand}}`;
  }

  if (n === 1) return `\\frac{\\sqrt{${radicand}}}{${d}}`;
  return `\\frac{${n}\\sqrt{${radicand}}}{${d}}`;
}

function fracSqrtPiLatex(num, den, radicand) {
  const g = gcd(num, den);
  const n = num / g;
  const d = den / g;

  if (d === 1) {
    if (n === 1) return `\\sqrt{${radicand}}\\pi`;
    return `${n}\\sqrt{${radicand}}\\pi`;
  }

  if (n === 1) return `\\frac{\\sqrt{${radicand}}\\pi}{${d}}`;
  return `\\frac{${n}\\sqrt{${radicand}}\\pi}{${d}}`;
}

function trigLatex(kind, halfAngle) {
  if (halfAngle === 30) {
    return kind === 'sin' ? '\\frac{1}{2}' : '\\frac{\\sqrt{3}}{2}';
  }
  if (halfAngle === 45) return '\\frac{\\sqrt{2}}{2}';
  if (halfAngle === 60) {
    return kind === 'sin' ? '\\frac{\\sqrt{3}}{2}' : '\\frac{1}{2}';
  }
  return `\\${kind} ${halfAngle}^{\\circ}`;
}

function buildExactData(slant, angleDeg) {
  const halfAngleDeg = angleDeg / 2;
  const k = slant / 2;

  if (angleDeg === 60) {
    return {
      halfAngleDeg,
      heightLatex: sqrt3Latex(k),
      radiusLatex: String(k),
      heightNumeric: k * Math.sqrt(3),
      radiusNumeric: k,
      volumeCoeffNumeric: (slant ** 3 * Math.sqrt(3)) / 24,
      volumeLatex: fracSqrtPiLatex(slant ** 3, 24, 3),
      volumeCoeffLatex: fracSqrtLatex(slant ** 3, 24, 3),
    };
  }

  if (angleDeg === 90) {
    return {
      halfAngleDeg,
      heightLatex: fracSqrtLatex(slant, 2, 2),
      radiusLatex: fracSqrtLatex(slant, 2, 2),
      heightNumeric: (slant * Math.sqrt(2)) / 2,
      radiusNumeric: (slant * Math.sqrt(2)) / 2,
      volumeCoeffNumeric: (slant ** 3 * Math.sqrt(2)) / 12,
      volumeLatex: fracSqrtPiLatex(slant ** 3, 12, 2),
      volumeCoeffLatex: fracSqrtLatex(slant ** 3, 12, 2),
    };
  }

  return {
    halfAngleDeg,
    heightLatex: String(k),
    radiusLatex: sqrt3Latex(k),
    heightNumeric: k,
    radiusNumeric: k * Math.sqrt(3),
    volumeCoeffNumeric: slant ** 3 / 8,
    volumeLatex: `${slant ** 3 / 8}\\pi`,
    volumeCoeffLatex: String(slant ** 3 / 8),
  };
}

/**
 * Wariant „oryginał” – matura podstawowa, maj 2025, zadanie 25:
 *   tworząca stożka = 8, kąt rozwarcia 120°.
 *   h = 8·cos60° = 4, r = 8·sin60° = 4√3, V = 1/3 π r² h = 64π.
 */
function createMay2025OriginalTask() {
  const exact = buildExactData(8, 120);
  return {
    type: 'MaturaConeVolumeOpeningOpenTask',
    data: { maxPoints: 3, slant: 8, angleDeg: 120, ...exact },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_MAJ',
      taskNumber: 25,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === 'original' &&
    generationOptions.session === '2025_MAJ'
  ) {
    return createMay2025OriginalTask();
  }

  const l = pickOne(SLANT_VALUES);
  const angleDeg = pickOne(ANGLE_VALUES);
  const exact = buildExactData(l, angleDeg);

  return {
    type: 'MaturaConeVolumeOpeningOpenTask',
    data: {
      maxPoints: 3,
      slant: l,
      angleDeg,
      ...exact,
    },
  };
}

export function renderTask({ data }) {
  const { slant, angleDeg } = data;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne - otwarte (0-3 pkt).</strong>
      </p>

      <p>
        Tworząca stożka ma długość <InlineMath math={String(slant)} />. Kąt
        rozwarcia tego stożka ma miarę{' '}
        <InlineMath math={`${angleDeg}^{\\circ}`} />.
      </p>

      <p>Oblicz objętość tego stożka. Zapisz obliczenia.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  const inputProps = {
    disabled,
    panelWidthDesktop: 360,
  };

  return (
    <div style={{ display: 'grid', rowGap: 18, maxWidth: 430 }}>
      <OpenTaskScoringHint variant="independent" />
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          Krok 1. Oblicz wysokość stożka: <InlineMath math={'h ='} />
        </div>
        <RootFractionInput
          value={ua.height}
          onChange={setField('height')}
          placeholder="np. 4 albo 4sqrt(3)"
          ariaLabel="wysokość stożka"
          label="h"
          {...inputProps}
        />
      </div>

      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          Krok 2. Oblicz promień podstawy: <InlineMath math={'r ='} />
        </div>
        <RootFractionInput
          value={ua.radius}
          onChange={setField('radius')}
          placeholder="np. 4sqrt(3)"
          ariaLabel="promień podstawy stożka"
          label="r"
          {...inputProps}
        />
      </div>

      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          Krok 3. Oblicz objętość stożka:{' '}
          <InlineMath math={'V ='} />
        </div>
        <RootFractionPiInput
          value={ua.volumeCoeff}
          onChange={setField('volumeCoeff')}
          placeholder="np. 64sqrt(3)pi/3"
          ariaLabel="objętość stożka z pi"
          label="V"
          {...inputProps}
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const hUser = parseNumberLike(ua.height);
  const rUser = parseNumberLike(ua.radius);
  const vUser = parseVolumeCoefficient(ua.volumeCoeff);
  const vHasPi = answerHasPi(ua.volumeCoeff);

  const heightOk = approxEqual(hUser, data.heightNumeric);
  const radiusOk = approxEqual(rUser, data.radiusNumeric);
  const volumeOk = vHasPi && approxEqual(vUser, data.volumeCoeffNumeric);

  const score = (heightOk ? 1 : 0) + (radiusOk ? 1 : 0) + (volumeOk ? 1 : 0);

  return {
    isCorrect: score === 3,
    score,
    maxScore: 3,
    parts: { height: heightOk, radius: radiusOk, volume: volumeOk },
    details: { heightOk, radiusOk, volumeOk },
  };
}

export function renderAnswer({ data }) {
  const { heightLatex, radiusLatex, volumeLatex } = data;

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      <InlineMath math={`h=${heightLatex}`} />
      <InlineMath math={`r=${radiusLatex}`} />
      <InlineMath math={`V=${volumeLatex}`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;

  const h = rawString(userAnswer.height);
  const r = rawString(userAnswer.radius);
  const v = rawString(userAnswer.volumeCoeff);
  const empty = !h.trim() && !r.trim() && !v.trim();

  if (empty) return <i>(brak odpowiedzi)</i>;

  const latexOrDash = (raw, label) => {
    if (!raw.trim()) return `${label}=\\text{-}`;
    return formatLatexPreview(raw, label) || `${label}=\\text{-}`;
  };

  const volumePreview = formatRootFractionPiPreview(v, 'V');

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      <InlineMath math={`\\displaystyle ${latexOrDash(h, 'h')}`} />
      <InlineMath math={`\\displaystyle ${latexOrDash(r, 'r')}`} />
      <InlineMath
        math={`\\displaystyle ${volumePreview || 'V=\\text{-}'}`}
      />
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    slant,
    halfAngleDeg,
    heightLatex,
    heightNumeric,
    radiusLatex,
    radiusNumeric,
    volumeLatex,
  } = data;
  const cosHalf = trigLatex('cos', halfAngleDeg);
  const sinHalf = trigLatex('sin', halfAngleDeg);

  return (
    <div className="space-y-4" style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1. Przekrój osiowy stożka.</strong>
      </p>
      <p>
        W przekroju osiowym stożka otrzymujemy trójkąt równoramienny. Jego
        ramionami są tworzące stożka, a wysokość stożka dzieli kąt rozwarcia
        na połowy. Dlatego w trójkącie prostokątnym mamy kąt{' '}
        <InlineMath math={`${halfAngleDeg}^{\\circ}`} /> i przeciwprostokątną
        równą <InlineMath math={String(slant)} />.
      </p>

      <ConeAxialHintDiagram
        rVal={radiusNumeric}
        hVal={heightNumeric}
        alphaLabel={`${halfAngleDeg}^{\\circ}`}
      />

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`h=${slant}\\cdot \\cos ${halfAngleDeg}^{\\circ}`} />
        <BlockMath math={`h=${slant}\\cdot ${cosHalf}=${heightLatex}`} />
      </div>

      <p>
        <strong>Krok 2. Obliczamy promień podstawy.</strong>
      </p>
      <p>
        Promień podstawy jest drugą przyprostokątną tego samego trójkąta
        prostokątnego.
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`r=${slant}\\cdot \\sin ${halfAngleDeg}^{\\circ}`} />
        <BlockMath math={`r=${slant}\\cdot ${sinHalf}=${radiusLatex}`} />
      </div>

      <p>
        <strong>Krok 3. Podstawiamy do wzoru na objętość stożka.</strong>
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={'V=\\frac{1}{3}\\pi r^2h'} />
        <BlockMath math={`V=\\frac{1}{3}\\pi\\cdot \\left(${radiusLatex}\\right)^2\\cdot ${heightLatex}`} />
        <BlockMath math={`V=${volumeLatex}`} />
      </div>
    </div>
  );
}
